import { BackendError } from "@exceptions";
import { useLanguage } from "@src/hooks/helperHooks";
import { useClearSessionMessages } from "@src/hooks/use-clear-session-messages";
import { useEndSession } from "@src/hooks/use-end-session";
import { queryKeys as organizationQueryKeys } from "@src/modules/member/hooks/organizations";
import { Me } from "@src/modules/member/models/me";
import { LOGIN_PATH } from "@src/modules/member/modules/main/router/paths";
import { toMemberPath } from "@src/modules/member/path";
import {
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  BackendResponseFormat,
  MutationDefaultOptions,
  backendFetch,
} from "@utils";
import { useTranslation } from "react-i18next";

const queryKeys = {
  me: "ME",
};

type UseSignup = UseMutationResult<
  BackendResponseFormat<unknown>,
  Error,
  {
    email: string;
    password: string;
    invitation_token?: string;
  },
  unknown
>;
export function useSignup(mutationOptions: MutationDefaultOptions): UseSignup {
  const {
    i18n: { language },
  } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      email: string;
      password: string;
      invitation_token?: string;
    }): Promise<BackendResponseFormat<unknown>> => {
      await backendFetch({
        url: `${import.meta.env.VITE_BACKEND_BASE_PATH}/sanctum/csrf-cookie`,
        method: "GET",
      });

      const response = await backendFetch({
        url: `${import.meta.env.VITE_BACKEND_BASE_PATH}/api/v0/member/me`,
        method: "POST",
        body: data,
        language,
      });

      return response as BackendResponseFormat<unknown>;
    },
    retry: 0,
    meta: {
      errorKey: mutationOptions.errorKey,
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.me] });
    },
  });
}

type UseRequestLoginResult = UseMutationResult<
  BackendResponseFormat<{ isTwoFaEnabled: boolean }>,
  Error,
  { email: string; password: string; invitation_token?: string },
  unknown
>;
export function useLogin(
  mutationOptions: MutationDefaultOptions,
): UseRequestLoginResult {
  const language = useLanguage();
  const queryClient = useQueryClient();
  const clearSessionMessages = useClearSessionMessages();

  return useMutation({
    mutationFn: async (data: { email: string; password: string }) => {
      await backendFetch({
        url: `${import.meta.env.VITE_BACKEND_BASE_PATH}/sanctum/csrf-cookie`,
        method: "GET",
      });

      const response = await backendFetch({
        url: `${import.meta.env.VITE_BACKEND_BASE_PATH}/api/v0/member/login`,
        method: "POST",
        body: data,
        language,
      });

      return response as BackendResponseFormat<{ isTwoFaEnabled: boolean }>;
    },
    retry: 0,
    meta: {
      errorKey: mutationOptions.errorKey,
    },
    onSuccess: () => {
      clearSessionMessages();
      queryClient.invalidateQueries({ queryKey: [queryKeys.me] });
      queryClient.refetchQueries({
        queryKey: [organizationQueryKeys.organizations],
      });
    },
  });
}

type UseMeResult = UseQueryResult<BackendResponseFormat<Me>, BackendError>;
export function useMe(): UseMeResult {
  const language = useLanguage();

  return useQuery({
    queryKey: [queryKeys.me],
    queryFn: async () => {
      const response = await backendFetch({
        url: `${import.meta.env.VITE_BACKEND_BASE_PATH}/api/v0/member/me`,
        method: "GET",
        language,
      });

      return response;
    },
    retry: 0,
    refetchOnWindowFocus: true,
  });
}

export interface LogoutDestination {
  to: string;
  state?: unknown;
}

export function useLogout(): UseMutationResult<
  void,
  Error,
  LogoutDestination | null,
  unknown
> {
  const endSession = useEndSession();

  return useMutation({
    mutationFn: async () => {
      await backendFetch({
        url: `${import.meta.env.VITE_BACKEND_BASE_PATH}/api/v0/member/logout`,
        method: "POST",
      });
    },
    retry: 0,
    onSettled: (_data, _error, destination) => {
      endSession(destination?.to ?? toMemberPath(LOGIN_PATH()), {
        state: destination?.state,
      });
    },
  });
}

export function useUploadImage(
  mutationOptions: MutationDefaultOptions,
): UseMutationResult<void, Error, FormData, unknown> {
  const queryClient = useQueryClient();
  const language = useLanguage();

  return useMutation({
    mutationFn: async (data: FormData) => {
      await backendFetch({
        url:
          import.meta.env.VITE_BACKEND_BASE_PATH +
          "/api/v0/member/me/upload-profile-image",
        method: "POST",
        body: data,
        multipart: true,
        language,
      });
    },
    retry: 0,
    meta: {
      errorKey: mutationOptions.errorKey,
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.me] });
    },
  });
}

export function useDeleteImage(
  mutationOptions: MutationDefaultOptions,
): UseMutationResult<void, Error, null, unknown> {
  const queryClient = useQueryClient();
  const language = useLanguage();

  return useMutation({
    mutationFn: async () => {
      await backendFetch({
        url:
          import.meta.env.VITE_BACKEND_BASE_PATH +
          "/api/v0/member/me/delete-profile-image",
        method: "DELETE",
        language,
      });
    },
    retry: 0,
    meta: {
      errorKey: mutationOptions.errorKey,
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.me] });
    },
  });
}

interface UpdateMeRequest {
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  street_address?: string | null;
  street_address_2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
}
type UseUpdateMeResult = UseMutationResult<
  BackendResponseFormat<Me> | string,
  Error,
  UpdateMeRequest,
  unknown
>;
export function useUpdateMe(
  mutationOptions: MutationDefaultOptions,
): UseUpdateMeResult {
  const queryClient = useQueryClient();
  const language = useLanguage();

  return useMutation({
    mutationFn: async (data: UpdateMeRequest) => {
      const response = await backendFetch({
        url: import.meta.env.VITE_BACKEND_BASE_PATH + "/api/v0/member/me",
        method: "PUT",
        body: data,
        language,
      });

      return response as { data: Me };
    },
    retry: 0,
    meta: {
      errorKey: mutationOptions.errorKey,
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.me] });
    },
  });
}

type UseUpdatePasswordResult = UseMutationResult<
  BackendResponseFormat<unknown> | string,
  Error,
  {
    old_password: string;
    new_password: string;
    new_password_confirmation: string;
  },
  unknown
>;
export function useUpdatePassword(
  mutationOptions: MutationDefaultOptions,
): UseUpdatePasswordResult {
  const queryClient = useQueryClient();
  const language = useLanguage();

  return useMutation({
    mutationFn: async (data: {
      old_password: string;
      new_password: string;
      new_password_confirmation: string;
    }) => {
      const response = await backendFetch({
        url:
          import.meta.env.VITE_BACKEND_BASE_PATH +
          "/api/v0/member/me/change-password",
        method: "PUT",
        body: data,
        language,
      });

      return response as BackendResponseFormat<unknown> | string;
    },
    retry: 0,
    meta: {
      errorKey: mutationOptions.errorKey,
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.me] });
    },
  });
}

type UseRequestPasswordResetResult = UseMutationResult<
  BackendResponseFormat<unknown>,
  Error,
  { email: string },
  unknown
>;

export function useRequestPasswordReset(
  mutationOptions: MutationDefaultOptions,
): UseRequestPasswordResetResult {
  const language = useLanguage();

  return useMutation({
    mutationFn: async (data: {
      email: string;
    }): Promise<BackendResponseFormat<unknown>> => {
      await backendFetch({
        url: import.meta.env.VITE_BACKEND_BASE_PATH + "/sanctum/csrf-cookie",
        method: "GET",
      });

      const response = await backendFetch({
        url:
          import.meta.env.VITE_BACKEND_BASE_PATH +
          "/api/v0/member/request-password-reset",
        method: "POST",
        body: data,
        language,
      });

      return response as BackendResponseFormat<unknown>;
    },
    retry: 0,
    meta: {
      errorKey: mutationOptions.errorKey,
    },
  });
}

type UseResetPasswordResult = UseMutationResult<
  BackendResponseFormat<unknown>,
  Error,
  {
    email: string;
    token: string;
    password: string;
  },
  unknown
>;

export function useResetPassword(
  mutationOptions: MutationDefaultOptions,
): UseResetPasswordResult {
  const language = useLanguage();

  return useMutation({
    mutationFn: async (data: {
      email: string;
      token: string;
      password: string;
    }): Promise<BackendResponseFormat<unknown>> => {
      await backendFetch({
        url: import.meta.env.VITE_BACKEND_BASE_PATH + "/sanctum/csrf-cookie",
        method: "GET",
      });

      const response = await backendFetch({
        url:
          import.meta.env.VITE_BACKEND_BASE_PATH +
          "/api/v0/member/reset-password",
        method: "POST",
        body: data,
        language,
      });

      return response as BackendResponseFormat<unknown>;
    },
    retry: 0,
    meta: {
      errorKey: mutationOptions.errorKey,
    },
  });
}

type UseResendVerificationEmailResult = UseMutationResult<
  void,
  Error,
  void,
  unknown
>;

export function useResendVerificationEmail(): UseResendVerificationEmailResult {
  const queryClient = useQueryClient();
  const language = useLanguage();

  return useMutation({
    mutationFn: async () => {
      await backendFetch({
        url: import.meta.env.VITE_BACKEND_BASE_PATH + "/sanctum/csrf-cookie",
        method: "GET",
      });

      await backendFetch({
        url:
          import.meta.env.VITE_BACKEND_BASE_PATH +
          "/api/v0/member/email-verification/resend",
        method: "POST",
        language,
      });
    },
    retry: 0,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.me] });
    },
  });
}

export function useVerifyEmail() {
  const queryClient = useQueryClient();
  const language = useLanguage();

  return useMutation({
    mutationFn: async ({ token, email }: { token: string; email: string }) => {
      await backendFetch({
        url: import.meta.env.VITE_BACKEND_BASE_PATH + "/sanctum/csrf-cookie",
        method: "GET",
      });

      await backendFetch({
        url:
          import.meta.env.VITE_BACKEND_BASE_PATH +
          "/api/v0/member/verify-email",
        method: "POST",
        body: {
          token,
          email,
        },
        language,
      });
    },
    retry: 0,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.me] });
    },
  });
}
